import { Trans, useLingui } from "@lingui/react/macro";
import React from "react";
import * as styles from "./CruiseExcursionsList.module.scss";
import { Button, Tag } from "@silversea-ssc/ui-components-react";
import DefaultShorexIcon from "components/ShorexIcon/DefaultShorexIcon";
import { FareCode } from "@silversea-ssc/web-fares";
import IncludedWithFaresBadge from "components/IncludedWithFaresBadge/IncludedWithFaresBadge";
import { CategorisedShorex } from "./types";
import ContentExcursionAccessibilityLevel from "./ContentExcursionAccessibilityLevel";
import ContentExcursionActivityLevel from "./ContentExcursionActivityLevel";
import clsx from "clsx";
import { useExcursionsMicroContent } from "features/shorex/ExcursionsMicroContentProvider";
import { useUnbundlingFeatureFlagEnabled } from "features/fare/unbundling-fares";
import {
  formatShorexPrice,
  shorexSplitNewFeature
} from "@silversea-ssc/system-components-react/ShorexDrawer/utils";
import { useGeolocation } from "src/utils/localization/geolocation/GeolocationContext";

const newShorexFeatureCode = "new";

export interface ExcursionItem extends CategorisedShorex {
  openModal?: () => void;
}

export interface CruiseExcursionsListProps {
  excursions: {
    title: JSX.Element | string;
    items: ExcursionItem[];
    shorexesActiveOnFares?: FareCode[];
  }[];
}

const CruiseExcursionItem: React.FC<React.PropsWithChildren<ExcursionItem>> = ({
  openModal,
  features,
  activityLevel,
  accessibility,
  name,
  code,
  subcategory,
  price,
  hideDurationAndCode,
  priceType
}) => {
  const { i18n } = useLingui();
  const geolocation = useGeolocation();
  const { features: microContentFeatures = [], subcategories = [] } =
    useExcursionsMicroContent();
  const { featureNew } = shorexSplitNewFeature(features, microContentFeatures);
  const isUnbundling = useUnbundlingFeatureFlagEnabled();

  const formattedPrice =
    isUnbundling && price
      ? formatShorexPrice(price, priceType, i18n, geolocation.defaultCurrency)
      : undefined;
  const subcategoriesContent = subcategories.find(
    (l) => l?.code === subcategory
  );

  if (isUnbundling) {
    return (
      <tr>
        <td className={styles.columnName}>
          <div className={styles.tags}>
            {subcategoriesContent?.label && (
              <div className={clsx(styles.tag, styles.subCategory)}>
                {subcategoriesContent.label.localized}
              </div>
            )}
            {featureNew?.label && (
              <Tag
                backgroundColor="neutral15"
                className={clsx(styles.tag, styles.newTag)}
              >
                {featureNew.label.localized}
              </Tag>
            )}
          </div>
          <span className={styles.title}>{name}</span>{" "}
          {formattedPrice && (
            <div className={styles.tags}>
              <div className={clsx(styles.tag, styles.price)}>
                {formattedPrice}
              </div>
            </div>
          )}
        </td>
        <td className={styles.columnActivityLevel}>
          {activityLevel && (
            <ContentExcursionActivityLevel level={activityLevel} />
          )}
        </td>
        <td className={styles.columnAccessibility}>
          {accessibility && (
            <ContentExcursionAccessibilityLevel level={accessibility} />
          )}
        </td>
        <td className={styles.columnIcons}>
          <div className={styles.icons}>
            {features
              ?.filter((x) => x !== newShorexFeatureCode)
              ?.map((i) => (
                <React.Fragment key={i}>
                  <DefaultShorexIcon
                    key={i}
                    code={i}
                    size="small"
                    showLabelMobile
                  />{" "}
                </React.Fragment>
              ))}
          </div>
        </td>
        <td className={styles.columnViewMore}>
          {openModal && (
            <Button
              variant="secondary"
              onClick={openModal}
              dataContentPiece={`view_excursion_${code}`}
            >
              <Trans>View</Trans>
            </Button>
          )}
        </td>
      </tr>
    );
  } else {
    return (
      <tr>
        <td className={styles.columnName}>
          <span className={styles.title}>{name}</span>{" "}
          {!hideDurationAndCode && code && (
            <React.Fragment>
              <span className={styles.code}>
                <Trans>Code</Trans>: {code}
              </span>{" "}
            </React.Fragment>
          )}
          {features?.includes(newShorexFeatureCode) && (
            <DefaultShorexIcon
              code="new"
              size="small"
              className={styles.iconNewInline}
            />
          )}
        </td>
        <td className={styles.columnNew}>
          {features?.includes(newShorexFeatureCode) && (
            <DefaultShorexIcon code="new" size="small" />
          )}
        </td>
        <td className={styles.columnActivityLevel}>
          {activityLevel && (
            <ContentExcursionActivityLevel level={activityLevel} />
          )}
        </td>
        <td className={styles.columnAccessibility}>
          {accessibility && (
            <ContentExcursionAccessibilityLevel level={accessibility} />
          )}
        </td>
        <td className={styles.columnIcons}>
          <div className={styles.icons}>
            {features
              ?.filter((x) => x !== newShorexFeatureCode)
              ?.map((i) => (
                <React.Fragment key={i}>
                  <DefaultShorexIcon
                    key={i}
                    code={i}
                    size="small"
                    showLabelMobile
                  />{" "}
                </React.Fragment>
              ))}
          </div>
        </td>
        <td className={styles.columnViewMore}>
          {openModal && (
            <Button
              variant="secondary"
              onClick={openModal}
              dataContentPiece={`view_excursion_${code}`}
            >
              <Trans>View</Trans>
            </Button>
          )}
        </td>
      </tr>
    );
  }
};

const CruiseExcursionList: React.FC<
  React.PropsWithChildren<CruiseExcursionsListProps>
> = ({ excursions = [] }) => (
  <React.Fragment>
    {excursions.some((e) => e.items.length > 0) && (
      <div className={styles.container}>
        <table className={styles.table}>
          {excursions.map((i, index) => {
            if (!i.items?.length) return null;

            return (
              <React.Fragment key={index}>
                <thead>
                  <tr>
                    <th colSpan={6} className={styles.sectionTitle}>
                      <div className={styles.sectionTitleContent}>
                        {i.title}{" "}
                        {i.shorexesActiveOnFares && (
                          <IncludedWithFaresBadge
                            fareCodes={i.shorexesActiveOnFares}
                            className={styles.shorexFareBadge}
                          />
                        )}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {i.items.map((i) => (
                    <CruiseExcursionItem
                      {...i}
                      key={i.name}
                      openModal={i.openModal}
                    />
                  ))}
                </tbody>
              </React.Fragment>
            );
          })}
        </table>
      </div>
    )}
  </React.Fragment>
);

export default CruiseExcursionList;
